import { CategoryScale, Chart as ChartJS } from "chart.js/auto";
import { PieChartData } from "@/types";
import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";

ChartJS.register(CategoryScale);

export default function PieChart({
  processSaving,
  riskSaving,
  environmentalSaving,
  setChartImage,
}: {
  processSaving: number;
  riskSaving: number;
  environmentalSaving: number;
  setChartImage: (image: string) => void;
}) {
  const { t } = useTranslation("report");
  const chartRef = useRef<HTMLDivElement | null>(null);

  const savingsData: PieChartData[] = [
    {
      id: 1,
      label: "Risk Savings",
      savings: riskSaving,
    },
    {
      id: 2,
      label: "Process Savings",
      savings: processSaving,
    },
    {
      id: 3,
      label: "Environmental Savings",
      savings: environmentalSaving,
    },
  ];
  const data = {
    datasets: [
      {
        data: savingsData.map((item) => item.savings),
        backgroundColor: ["#5a0377", "#ed1164", "#148c5a"],
      },
    ],
  };

  useEffect(() => {
    // Capture the chart for the PDF after the scale animation has settled. We composite the Chart.js
    // canvas onto a #002744-filled canvas (rather than html2canvas-ing the wrapper div) so the PDF gets
    // the dark background without rasterising the surrounding DOM — html2canvas walks computed CSS and
    // throws on Tailwind v4's oklch colours (RW: "Download PDF" fix). No extra dependency, more reliable.
    const timer = setTimeout(() => {
      const chartCanvas = chartRef.current?.querySelector("canvas");
      if (!chartCanvas) return;

      const composite = document.createElement("canvas");
      composite.width = chartCanvas.width;
      composite.height = chartCanvas.height;
      const ctx = composite.getContext("2d");
      if (!ctx) return;

      ctx.fillStyle = "#002744";
      ctx.fillRect(0, 0, composite.width, composite.height);
      ctx.drawImage(chartCanvas, 0, 0);

      setChartImage(composite.toDataURL("image/png"));
    }, 1700);

    return () => clearTimeout(timer);
  }, [setChartImage, processSaving, riskSaving, environmentalSaving]);

  return (
    <div className="relative mx-auto mb-6 mt-4 px-8 sm:w-[90%] md:w-[60%] md:px-0 lg:mx-0 lg:w-[40%]">
      <p className="absolute left-1/2 top-1/2 mt-2 translate-x-[-50%] translate-y-[-50%] whitespace-pre-wrap text-center text-lg font-semibold uppercase lg:text-2xl lg:tracking-wider">
        {t("graph.potential_savings")}
      </p>

      <div ref={chartRef}>
        <Doughnut data={data} options={{ animation: { animateScale: true } }} />
      </div>
    </div>
  );
}
